import React, { useEffect, useState } from "react";
import { Row, Col, CardBody, Card, Button, Input } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Driver } from "models/Driver";
import "../../assets/css/custom.css";
import { Puff } from "react-loader-spinner";
import { Select } from "antd";
import moment from "moment";
import ReactPaginate from "react-paginate";

toast.configure();

interface AvailabilitySlot {
  _id: string;
  driverId: string;
  year: number;
  weekNumber: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  date: string;
  repeatWeekly: boolean;
  isAllDay: boolean;
  isActive: boolean;
}

interface DriverWithAvailability extends Driver {
  availabilitySlots?: {
    currentWeek: AvailabilitySlot[];
    nextWeek: AvailabilitySlot[];
  };
  acceptedShifts?: AcceptedShift[];
}

interface AcceptedShift {
  _id: string;
  acceptedDrivers: string[];
  status: string;
  startDateTime?: string;  
  endDateTime?: string;    
  requiredDrivers?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

const DAYS_OF_WEEK = [
  { key: 0, label: "MON", fullName: "Monday" },
  { key: 1, label: "TUE", fullName: "Tuesday" },
  { key: 2, label: "WED", fullName: "Wednesday" },
  { key: 3, label: "THU", fullName: "Thursday" },
  { key: 4, label: "FRI", fullName: "Friday" },
  { key: 5, label: "SAT", fullName: "Saturday" },
  { key: 6, label: "SUN", fullName: "Sunday" },
];

const AvailableDriverSlot: React.FC = () => {
  const [drivers, setDrivers] = useState<DriverWithAvailability[]>([]);
  const [isloading, setIsloading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"current" | "upcoming">("current");
  const [LIMIT_P_C, setLIMIT_P_C] = useState(10);
  const [isOffset_p_c, setOffset_p_c] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [searchText, setSearchText] = useState("");
  const [currentWeekDates, setCurrentWeekDates] = useState<string[]>([]);
  const [nextWeekDates, setNextWeekDates] = useState<string[]>([]);
  const [seeMore_p_c, setSeeMore_p_c] = useState(false);

  useEffect(() => {
    // Calculate week dates using ISO week (Monday as first day)
    const startOfCurrentWeek = moment().startOf("isoWeek");
    const startOfNextWeek = moment().add(1, "week").startOf("isoWeek");

    const currentDates = Array.from({ length: 7 }, (_, i) =>
      startOfCurrentWeek.clone().add(i, "days").format("DD")
    );

    const nextDates = Array.from({ length: 7 }, (_, i) =>
      startOfNextWeek.clone().add(i, "days").format("DD")
    );

    setCurrentWeekDates(currentDates);
    setNextWeekDates(nextDates);
  }, []);

  useEffect(() => {
    fetchDriversWithAvailability();
  }, [LIMIT_P_C, isOffset_p_c]);

  const fetchDriversWithAvailability = async () => {
    setIsloading(true);
    try {
      console.log("Fetching drivers with availability...", {
        offset: isOffset_p_c,
        limit: LIMIT_P_C
      });
      
      // FIXED: Correct parameter order (offset, limit)
      const res = await AdminService.getDriversWithAvailability(LIMIT_P_C , isOffset_p_c); 
      
      console.log("API Response:", res);
      
      if (res.success) {
        const driverList = res.data?.driverList || res.data || [];
        const totalCount = res.data?.count || res.data?.total || driverList.length;
        
        console.log("Drivers fetched:", driverList.length, "Total:", totalCount);
        
        setDrivers(driverList);
        setSeeMore_p_c(!(driverList.length > 0 && driverList.length === LIMIT_P_C));
        setPageCount(Math.ceil(totalCount / LIMIT_P_C));
      } else {
        console.error("API returned success=false:", res);
        toast.error(res.message || "Failed to fetch drivers");
        setDrivers([]);
        setPageCount(1);
      }
    } catch (error: any) {
      console.error("Error fetching drivers:", error);
      toast.error(`Failed to fetch drivers: ${error.message || error}`);
      setDrivers([]);
      setPageCount(1);
    } finally {
      setIsloading(false);
    }
  };

  const handlePageChange = (page: any) => {
    setOffset_p_c(page.selected + 1);
  };

  const handleSearch = async (value: string) => {
    setSearchText(value);
    
    if (value.trim() === "") {
      // If search is cleared, fetch all drivers
      setOffset_p_c(1);
      fetchDriversWithAvailability();
    } else {
      // Search drivers with availability
      setIsloading(true);
      try {
        const searchdata = {
          searchableString: value,
        };
        
        console.log("Searching drivers with:", searchdata);
        
        // FIXED: Correct parameter order for search too
        const res = await AdminService.searchDriversWithAvailability(
          searchdata, 
          LIMIT_P_C,
          isOffset_p_c
        );
        
        console.log("Search results:", res);
        
        if (res.success) {
          const searchResults = res.data || [];
          const totalCount = res.data?.count || res.data?.total || searchResults.length;
          
          console.log("Search returned:", searchResults.length, "drivers");
          setDrivers(searchResults);
          setSeeMore_p_c(!(searchResults.length > 0 && searchResults.length === LIMIT_P_C));
          setPageCount(Math.ceil(totalCount / LIMIT_P_C));
        } else {
          console.error("Search returned success=false:", res);
          toast.error(res.message || "Search failed");
          setDrivers([]);
          setPageCount(1);
        }
      } catch (error: any) {
        console.error("Search error:", error);
        toast.error(`Search failed: ${error.message || error}`);
        setDrivers([]);
        setPageCount(1);
      } finally {
        setIsloading(false);
      }
    }
  };

  const getAcceptedShiftsForDay = (
  driver: DriverWithAvailability,
  dayIndex: number,
  week: "current" | "upcoming"
): AcceptedShift[] => {
  if (!driver.acceptedShifts || driver.acceptedShifts.length === 0) {
    return [];
  }

  const startOfWeek = week === "current" 
    ? moment().startOf("isoWeek")
    : moment().add(1, "week").startOf("isoWeek");
  
  const targetDate = startOfWeek.clone().add(dayIndex, "days");
  const targetDateStr = targetDate.format("YYYY-MM-DD");

  return driver.acceptedShifts.filter((shift: any) => {
    // Check startDateTime instead of shiftDate
    if (shift.startDateTime) {
      const shiftDate = moment(shift.startDateTime).format("YYYY-MM-DD");
      return shiftDate === targetDateStr;
    }
    return false;
  });
};

  const getAvailabilitySlotsForDay = (
    driver: DriverWithAvailability,
    dayIndex: number,
    week: "current" | "upcoming"
  ): AvailabilitySlot[] => {
    const slots =
      week === "current"
        ? driver.availabilitySlots?.currentWeek || []
        : driver.availabilitySlots?.nextWeek || [];

    return slots.filter((slot) => slot.dayOfWeek === dayIndex);
  };

  const formatTimeSlot = (slot: AvailabilitySlot)=> {
    const timeString = `${slot.startTime}-${slot.endTime}`;
    
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
        {slot.repeatWeekly && (
          <span title="Repeats weekly" style={{ fontSize: "10px" }}>🔁</span>
        )}
        {slot.isAllDay && !slot.repeatWeekly && (
          <span title="All day" style={{ fontSize: "10px" }}>🌞</span>
        )}
        <span>{timeString}</span>
      </div>
    );
  };


  const handleColumnChange = (value: number) => {
    setLIMIT_P_C(value);
    setOffset_p_c(1); // Reset to first page when changing page size
  };

  const renderAvailabilityCell = (
  driver: DriverWithAvailability,
  dayIndex: number
) => {
  const week = activeTab === "current" ? "current" : "upcoming";
  const slots = getAvailabilitySlotsForDay(driver, dayIndex, week);
  const acceptedShifts = getAcceptedShiftsForDay(driver, dayIndex, week);

  // If no data at all
  if (slots.length === 0 && acceptedShifts.length === 0) {
    return (
      <td 
        key={dayIndex} 
        style={{ 
          textAlign: "center",
          padding: "12px 8px",
          fontSize: "13px",
          color: "#999",
          borderRight: "1px solid #dee2e6",
          verticalAlign: "middle",
          minWidth: "100px",
          backgroundColor: "#fafafa"
        }}
      >
        N/A
      </td>
    );
  }

  return (
    <td 
      key={dayIndex} 
      style={{ 
        textAlign: "center",
        padding: "8px 6px",
        fontSize: "12px",
        borderRight: "1px solid #dee2e6",
        verticalAlign: "middle",
        lineHeight: "1.5",
        minWidth: "100px"
      }}
    >
      {slots.map((slot, idx) => {
        const isRepeating = slot.repeatWeekly;
        const isAllDay = slot.isAllDay;
        
          // Determine cell styling based on slot type
        let cellStyle = {
          backgroundColor: "#ffffff",
          border: "1px solid #e0e0e0",
          color: "#333"
        };
        
        if (isRepeating && isAllDay) {
          cellStyle = {
            backgroundColor: "#e8f5e8",
            border: "1px solid #4caf50",
            color: "#1b5e20"
          };
        } else if (isRepeating) {
          cellStyle = {
            backgroundColor: "#e3f2fd",
            border: "1px solid #2196f3",
            color: "#0d47a1"
          };
        } else if (isAllDay) {
          cellStyle = {
            backgroundColor: "#e8f5e8",
            border: "1px solid #81c784",
            color: "#2e7d32"
          };
        }
        
        return (
          <div 
            key={`slot-${idx}`} 
            style={{ 
                marginBottom: "6px",
              ...cellStyle,
              padding: "6px 4px",
              borderRadius: "4px",
              fontWeight: "500",
              position: "relative"
            }}
            title={`${slot.startTime}-${slot.endTime}${isAllDay ? " (All Day)" : ""}${isRepeating ? " • Repeats weekly" : ""}`}
          >
            {formatTimeSlot(slot)}
            {isRepeating && (
              <div 
                style={{
                  fontSize: "9px",
                  fontWeight: "bold",
                  marginTop: "2px",
                  opacity: 0.8
                }}
              >
                Repeats
              </div>
            )}
          </div>
        );
      })}

      {acceptedShifts.map((shift: any, idx) => {
  const startTime = shift.startDateTime ? moment.utc(shift.startDateTime).format("HH:mm") : "";
  const endTime = shift.endDateTime ? moment.utc(shift.endDateTime).format("HH:mm") : "";
  const timeString = startTime && endTime 
    ? `${startTime}-${endTime}`
    : "Shift";
          
        return (
          <div 
            key={`shift-${idx}`} 
            style={{ 
              marginTop: slots.length > 0 ? "6px" : 0,
              marginBottom: idx < acceptedShifts.length - 1 ? "6px" : 0,
              backgroundColor: "#fff3e0",
              border: "2px solid #ff9800",
              color: "#e65100",
              padding: "6px 4px",
              borderRadius: "4px",
              fontWeight: "600"
            }}
            title={`Accepted Shift - Status: ${shift.status}`}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
              <span title="Accepted Shift" style={{ fontSize: "10px" }}>✓</span>
              <span>{timeString}</span>
            </div>
            <div 
              style={{
                fontSize: "9px",
                fontWeight: "bold",
                marginTop: "2px",
                opacity: 0.9
              }}
            >
              ACCEPTED
            </div>
          </div>
        );
      })}
    </td>
  );
};

  document.title = "Available Drivers | Zulu Courier";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs breadcrumbItem="Available Drivers / Availability Slot" />
        <Col lg={12}>
          <Card>
            <CardBody>
              {/* Header Section */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0" style={{ fontWeight: "600", fontSize: "16px" }}>
                    Available Driver List
                  </h5>
                  <p style={{ fontSize: "12px", color: "#666", margin: "4px 0 0 0" }}>
                    All Day slots shown with green background • 🔁 indicates repeating weekly slots
                  </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <Input
                    type="text"
                    placeholder="Search drivers..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{ 
                      width: "200px",
                      fontSize: "14px",
                      height: "38px"
                    }}
                  />
                  <Select
                    value={LIMIT_P_C}
                    onChange={handleColumnChange}
                    style={{ width: "120px" }}
                  >
                    <Select.Option value={10}>10 Rows</Select.Option>
                    <Select.Option value={25}>25 Rows</Select.Option>
                    <Select.Option value={50}>50 Rows</Select.Option>
                    <Select.Option value={100}>100 Rows</Select.Option>
                  </Select>
                </div>
              </div>

              {/* Tab Buttons */}
              <div className="mb-3">
                <Button
                  color={activeTab === "current" ? "primary" : "light"}
                  onClick={() => setActiveTab("current")}
                  style={{
                    backgroundColor: activeTab === "current" ? "#f75d21" : "#e0e0e0",
                    borderColor: activeTab === "current" ? "#f75d21" : "#e0e0e0",
                    color: activeTab === "current" ? "white" : "#666",
                    marginRight: "8px",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "8px 24px"
                  }}
                >
                  Current Week
                </Button>
                <Button
                  color={activeTab === "upcoming" ? "primary" : "light"}
                  onClick={() => setActiveTab("upcoming")}
                  style={{
                    backgroundColor: activeTab === "upcoming" ? "#f75d21" : "#e0e0e0",
                    borderColor: activeTab === "upcoming" ? "#f75d21" : "#e0e0e0",
                    color: activeTab === "upcoming" ? "white" : "#666",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: "500",
                    padding: "8px 24px"
                  }}
                >
                  Upcoming Week
                </Button>
              </div>

              {/* Legend Section */}
              <div className="mb-3 d-flex gap-3 align-items-center flex-wrap" style={{ fontSize: "12px", color: "#666", padding: "8px 12px", backgroundColor: "#f8f9fa", borderRadius: "4px" }}>
                <span style={{ fontWeight: "600" }}>Slot Types:</span>
                <div className="d-flex align-items-center gap-1">
                  <div style={{ width: "12px", height: "12px", backgroundColor: "#e3f2fd", border: "1px solid #2196f3", borderRadius: "2px" }}></div>
                  <span>Repeating weekly slot</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <div style={{ width: "12px", height: "12px", backgroundColor: "#e8f5e8", border: "1px solid #81c784", borderRadius: "2px" }}></div>
                  <span>All Day slot</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <div style={{ width: "12px", height: "12px", backgroundColor: "#e8f5e8", border: "1px solid #4caf50", borderRadius: "2px" }}></div>
                  <span>All Day + Repeating</span>
                </div>
                <div className="d-flex align-items-center gap-1">
                  <span>🔁</span>
                  <span>Repeating indicator</span>
                </div>
              </div>

              {/* Table */}
              {isloading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "500px" }}
                >
                  <Puff color="#FFD0B8" height={50} width={50} />
                </div>
              ) : (
                <>
                  {drivers.length > 0 ? (
                    <>
                      <div className="table-responsive" style={{ overflowX: "auto" }}>
                        <table 
                          className="table table-bordered table-hover"
                          style={{ 
                            fontSize: "13px",
                            marginBottom: 0
                          }}
                        >
                          <thead style={{ backgroundColor: "#f8f9fa" }}>
                            <tr>
                              <th 
                                style={{ 
                                  minWidth: "100px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}
                              >
                                Driver Id
                              </th>
                              <th 
                                style={{ 
                                  minWidth: "120px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}
                              >
                                Driver Name
                              </th>
                              <th 
                                style={{ 
                                  minWidth: "130px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}
                              >
                                Phone Number
                              </th>
                              <th 
                                style={{ 
                                  minWidth: "120px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}
                              >
                                Address
                              </th>
                              <th 
                                style={{ 
                                  minWidth: "110px",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}
                              >
                                Postal Code
                              </th>
                              <th
                                colSpan={7}
                                style={{
                                  backgroundColor: "#f0f0f0",
                                  fontWeight: "600",
                                  fontSize: "13px",
                                  padding: "12px",
                                  textAlign: "center",
                                  verticalAlign: "middle"
                                }}
                              >
                                Availability
                              </th>
                            </tr>
                            <tr>
                              <th colSpan={5} style={{ padding: 0, border: "none" }}></th>
                              {DAYS_OF_WEEK.map((day, idx) => (
                                <th 
                                  key={day.key} 
                                  style={{ 
                                    minWidth: "100px",
                                    textAlign: "center",
                                    padding: "10px 8px",
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    borderRight: "1px solid #dee2e6",
                                    backgroundColor: "#f8f9fa",
                                    verticalAlign: "middle"
                                  }}
                                >
                                  <div style={{ marginBottom: "4px" }}>{day.label}</div>
                                  <div style={{ fontSize: "12px", fontWeight: "normal", color: "#666" }}>
                                    {activeTab === "current"
                                      ? currentWeekDates[idx]
                                      : nextWeekDates[idx]}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {drivers.map((driver) => (
                              <tr key={driver._id}>
                                <td style={{ 
                                  padding: "12px",
                                  fontSize: "13px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}>
                                  {driver.customerId}
                                </td>
                                <td style={{ 
                                  padding: "12px",
                                  fontSize: "13px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}>
                                  {`${driver.firstName} ${driver.lastName || ""}`}
                                </td>
                                <td style={{ 
                                  padding: "12px",
                                  fontSize: "13px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}>
                                  {driver.phoneNumber}
                                </td>
                                <td style={{ 
                                  padding: "12px",
                                  fontSize: "13px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}>
                                  {driver.address?.city
                                    ? driver.address.city.split(",").slice(-3)[0] || ""
                                    : ""}
                                </td>
                                <td style={{ 
                                  padding: "12px",
                                  fontSize: "13px",
                                  borderRight: "1px solid #dee2e6",
                                  verticalAlign: "middle"
                                }}>
                                  {driver.address?.postalCode || ""}
                                </td>
                                {DAYS_OF_WEEK.map((day) =>
                                  renderAvailabilityCell(driver, day.key)
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination */}
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                          <ReactPaginate
                            previousLabel={"← Previous"}
                            nextLabel={"Next →"}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={"pagination"}
                            previousLinkClassName={"page-link"}
                            nextLinkClassName={"page-link"}
                            disabledClassName={"disabled"}
                            activeClassName={"active"}
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            forcePage={isOffset_p_c - 1}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <Row className="justify-content-center align-items-center" style={{ padding: "40px" }}>
                      <Col className="col-md-auto">
                        <div style={{ color: "#999", fontSize: "14px" }}>
                          {searchText ? "No drivers found matching your search" : "No Drivers to show"}
                        </div>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </div>
    </div>
  );
};

export default AvailableDriverSlot;